import Map from 'es6-map';
import axios from 'axios';
import firebase from './firebase';

const CancelToken = axios.CancelToken;

class Api {

    constructor(baseURL) {

        this.map = new Map();
        this.axios = axios.create({ baseURL });
        this.idToken = null;
        this.onErrorMessage = () => {};
        this.updateToken = () => Promise.reject(new Error('Unimplemented'));

        this.axios.interceptors.request.use(config => {

            if (this.idToken) {
                config.headers['Authorization'] = `Bearer ${this.idToken}`;
            }

            return config;

        }, error => {

            return Promise.reject(error);
        });

        this.axios.interceptors.response.use(response => {

            for (const [key, cancelToken] of this.map) {

                if (response.config.cancelToken === cancelToken) {
                    this.map.delete(key);
                    break;
                }
            }

            return response.data;

        }, error => {

            if (!error.response) {
                return Promise.reject(error);
            }

            if (error.response.status === 401 && !error.response.config.retried) {

                if (!firebase.auth().currentUser) {
                    return null;
                }

                return firebase.auth().currentUser.getIdToken(true).then(idToken => {

                    this.idToken = idToken;

                    error.response.config.retried = true;
                    return this.axios(error.response.config);
                });
            }

            for (const [key, cancelToken] of this.map) {

                if (error.response.config.cancelToken === cancelToken) {
                    this.map.delete(key);
                    break;
                }
            }

            if (!axios.isCancel(error)) {

                this.onErrorMessage(error.response.data.message || 'An unknown error occurred.');

                return Promise.reject(error.response.data);
            }
        });
    }

    call(config) {

        const source = CancelToken.source();
        config.cancelToken = source.token;

        const call = this.axios(config);

        this.map.set(call, source);

        return call;
    }

    cancel(p) {

        const source = this.map.get(p || Promise.resolve());

        if (source) {
            source.cancel();
        }
    }

    setOnErrorMessage = onErrorMessage => {

        this.onErrorMessage = onErrorMessage;
    };

    getStripeCustomer = () => {

        return this.call({ url: '/stripe-customer' });
    };

    updateCard = data => {

        return this.call({
            url: '/stripe-customer/update-card',
            method: 'post',
            data
        });
    };

    getProjectComments = params => {

        const { projectId } = params;

        delete params.projectId;

        return this.call({ url: `/projects/${projectId}/comments`, params });
    };

    getUsage = params => {

        const { projectId } = params;

        delete params.projectId;

        return this.call({ url: `/projects/${projectId}/usage`, params });
    };

    getUserHistory = params => {

        const { projectId, userId } = params;

        delete params.projectId;
        delete params.userId;

        return this.call({ url: `/projects/${projectId}/comments/history/${userId}`, params })
    };

    getProjects = params => {

        return this.call({ url: '/projects', params });
    };

    saveProject = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: projectId ? `/projects/${projectId}` : '/projects',
            method: projectId ? 'put' : 'post',
            data
        });
    };

    removeProject = params => {

        const { projectId } = params;

        delete params.projectId;

        return this.call({ url: `/projects/${projectId}`, method: 'delete' });
    };

    removeComments = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/comments/remove`,
            method: 'post',
            data
        });
    };

    approveComments = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/comments/approve`,
            method: 'post',
            data
        });
    };

    clearFlaggedComments = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/comments/clear-flagged`,
            method: 'post',
            data
        });
    };

    getModeratorRequests = () => {

        return this.call({
            url: '/moderator-requests',
            method: 'get'
        });
    };

    acceptModeratorRequest = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/moderator-requests/${projectId}/accept`,
            method: 'post',
            data
        });
    };

    rejectModeratorRequest = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/moderator-requests/${projectId}/reject`,
            method: 'post',
            data
        });
    };

    getBlockedUser = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/blocked-user/fetch`,
            method: 'post',
            data
        });
    };

    unblockUser = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/blocked-user/unblock`,
            method: 'post',
            data
        });
    };

    blockUser = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/blocked-user`,
            method: 'post',
            data
        });
    };

    regenerateWebhookCredentials = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/regenerate-webhook-credentials`,
            method: 'post',
            data
        });
    };

    regenerateSingleSignOnCredentials = data => {

        const { projectId } = data;

        delete data.projectId;

        return this.call({
            url: `/projects/${projectId}/regenerate-single-sign-on-credentials`,
            method: 'post',
            data
        });
    };

    getProjectPages = params => {

        const { projectId } = params;

        delete params.projectId;

        return this.call({ url: `/projects/${projectId}/pages`, params });
    };

    getPage = fileName => {

        return fetch(`https://storage.googleapis.com/total-comments.storage.commentbox.io/${fileName}?alt=media`)
            .then(response => {

                if(!response.ok) {
                    throw new Error();
                }
                return response.text();

            }).catch(() => '');

    };

}

export default new Api(`https://${process.env.REACT_APP_FIREBASE_PROJECT_ID}.appspot.com`);
//export default new Api(`http://localhost:9000`);


// WEBPACK FOOTER //
// ./src/utils/api.js